import { measurementMetrics, programTypes } from 'boot/globals';
import { Timestamp } from 'firebase/firestore';
import { useTime } from 'src/composables/time';
import { PROGRAM_STATUSES } from 'src/constants/programs';
import { FIELD_PREFIXES } from 'src/domains/reporting/constants';
import { calculateAge } from 'src/utils/DateUtils';
import { getCountrySubdivisions } from 'src/utils/IntlUtils';
import { getPlannedMasteryDate } from 'src/utils/PlannedMasteryUtils';
import { getProgramCompletionPercentage } from 'src/utils/ProgramUtils';
import { personalize } from 'src/utils/TextUtils';

/** @typedef {import('src/constants/programs.js').ProgramStatus} ProgramStatus */

export function resolveCustomDataFieldPresets(dataFields, category) {
  return dataFields
    ? dataFields
      .sort((a, b) => (a.order - b.order))
      .map((dataField) => ({
        prefix: FIELD_PREFIXES.DATAFIELD,
        token: `${category}.${FIELD_PREFIXES.DATAFIELD}${dataField.id}`,
        id: dataField.id,
        label: dataField.label,
        hint: null,
        category,
        type: dataField.type,
        allowMultiLines: dataField.allowMultiLines,
        allowMultipleOptions: dataField.allowMultipleOptions,
      }))
    : [];
} // resolveCustomDataFieldPresets()

function personalizeField(text, currentClient) {
  return personalize(
    text,
    currentClient?.firstName || currentClient?.clientName,
    currentClient?.gender,
  );
} // personalizeField()

export function formatField(value, type, field, i18n) {
  const { getLocaleDateString } = useTime();

  switch (type) {
    case 'select': {
      if (Array.isArray(value)) {
        return value.join(', ');
      }
      return value ?? '';
    }
    case 'yesNo': {
      if (value === null || value === undefined) {
        return i18n.t('common.notSelected');
      }
      return value ? i18n.t('common.buttonYes') : i18n.t('common.buttonNo');
    }
    case 'user':
      return value?.userName ?? '';
    case 'jsDate':
      return value instanceof Date ? getLocaleDateString(value) : null;
    case 'date':
      return value instanceof Timestamp ? getLocaleDateString(value.toDate()) : null;
    case 'textWithFallback':
      return value ?? (field.labelKey ? `[${i18n.t(field.labelKey)}]` : '');
    case 'imageFile':
      return value?.name ?? null;
    case 'text':
      return value ?? '';
    default:
      return value ?? null;
  }
} // formatField()

/**
 * @param {Record<string, unknown>} program
 * @param {ProgramStatus} status
 * @returns {string | null}
 */
function statusChangeDate(program, status) {
  return formatField(program?.statusChanges?.[status], 'date') || '';
} // statusChangeDate()

export const fieldCalculators = {

  age: ({ client }) => {
    return calculateAge(client?.birthDate);
  },
  stateName: ({ client }) => {
    const subdivisions = getCountrySubdivisions(client?.country);
    if (subdivisions.length && client?.state) {
      return subdivisions.find((s) => s.code === client.state)?.label;
    }
    return '';
  },
  countryName: ({ client, i18n }) => {
    return client?.country ? i18n.t(client.country) : '';
  },
  genderLabel: ({ client, i18n }) => {
    if (!client?.gender) {
      return '';
    }
    const genderLabels = i18n.tm('clients.modals.editClient.gender');
    return genderLabels[client.gender] ?? '';
  },

  // Program field calculators
  programIndex: ({ programIndex }) => (
    String(programIndex + 1)
  ),
  treatmentAreaLabel: ({ program, clinicTreatmentAreas }) => (
    clinicTreatmentAreas || []
  )
    .find(({ id }) => id === program?.treatmentArea)
    ?.label || '',

  treatmentAreaCategoryLabel: ({ program, clinicTreatmentAreas }) => (
    clinicTreatmentAreas || []
  )
    .find(({ id }) => id === program?.treatmentArea)
    ?.subTreatmentAreasLabel || '',

  subTreatmentAreaLabel: ({ program, clinicTreatmentAreas }) => (
    clinicTreatmentAreas || [])
    .find(({ id }) => id === program?.subTreatmentArea)
    ?.label || '',

  baselineLabel: ({ program, currentClient }) => (personalizeField(program.baseline, currentClient)),
  objectiveLabel: ({ program, currentClient }) => (personalizeField(program.objective, currentClient)),

  statusChange_baseline: ({ program }) => statusChangeDate(program, PROGRAM_STATUSES.BASELINE),
  statusChange_active: ({ program }) => statusChangeDate(program, PROGRAM_STATUSES.ACTIVE),
  statusChange_maintenance: ({ program }) => statusChangeDate(program, PROGRAM_STATUSES.MAINTENANCE),
  statusChange_closed: ({ program }) => statusChangeDate(program, PROGRAM_STATUSES.CLOSED),
  statusChange_hold: ({ program }) => statusChangeDate(program, PROGRAM_STATUSES.HOLD),
  statusChange_discontinued: ({ program }) => statusChangeDate(program, PROGRAM_STATUSES.DISCONTINUED),

  plannedMasteryDate: ({ program }) => {
    const { addToDate } = useTime();
    return formatField(getPlannedMasteryDate(program, addToDate), 'date') || '';
  },
  plannedMasteryTimeframe: ({ program, i18n }) => {
    const tf = program?.plannedMasteryTimeframe;
    if (!tf?.quantity || !tf?.unit) return '';
    return i18n.tc(`editProgram.masteryTimeFrames.${tf.unit}`, tf.quantity, { count: tf.quantity });
  },

  // Report field calculators
  dateRangeFrom: ({ reportDateRange }) => formatField(reportDateRange?.fromTimestamp, 'jsDate') || '',
  dateRangeTo: ({ reportDateRange }) => formatField(reportDateRange?.toTimestamp, 'jsDate') || '',

  pageCount: ({ page, field, i18n }) => formatField(page?.pageCount, 'textWithFallback', field, i18n) || '',
  currentPage: ({ page, field, i18n }) => formatField(page?.currentPage, 'textWithFallback', field, i18n) || '',

  nowDate: () => {
    const now = new Date();
    const { getLocaleDateString } = useTime();
    return getLocaleDateString(now);
  },

  programPrimaryMetric: ({ program }) => {
    const metricId = program?.primaryMetric || programTypes[program?.type]?.defaultMetric;
    return measurementMetrics.find((metric) => metric.id === metricId)?.label ?? null;
  },

  programMasteryCriteria: ({ program, workflows, i18n }) => {
    if (!program?.workflow) {
      return i18n.t('common.notSelected');
    }
    return (workflows || []).find(({ id }) => id === program.workflow)?.workflowName || '';
  },

  // Mirrors the Progress page, which shows a percentage for Active programs and the status otherwise.
  programCompletionPercentage: ({ program, i18n }) => {
    if (!program) {
      return '';
    }
    if (program.status !== PROGRAM_STATUSES.ACTIVE) {
      return i18n.t(`programStatus_${program.status}`);
    }
    return `${getProgramCompletionPercentage(program.targetsPerStatus) ?? 0}%`;
  },

};

export function fieldCatgoriesToTokens(fieldCategories) {
  return fieldCategories.flatMap((cat) => cat.fields);
} // fieldCatgoriesToTokens()

function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
} // escapeRegExp()

function replaceTags(string, data = {}) {
  const plainTextString = string.replace(/\n/g, '<br/>');
  return Object
    .entries(data)
    .reduce(
      (res, [key, value]) => res.replace(new RegExp(`{{${key}}}`, 'g'), value),
      plainTextString,
    );
} // replaceTags()

function replaceHtml(string, data = {}) {
  /* eslint-disable max-len */
  return Object
    .entries(data)
    .reduce(
      (res, [key, value]) => res
        .replace(
          new RegExp(`<span (data-type="(dynamicField|dataSection)")? data-field="${key}" data-label="([^"]*)" class="([^"]*)"[^>]*></span>`, 'g'),
          value,
        ),
      string,
    );
  /* eslint-enable max-len */
} // replaceHtml()

export function getFieldTokenHtml(tokenField, tokenLabel, tokenType = 'dynamicField') {
  const contentClass = (tokenType === 'dataSection') ? 'data-section' : 'token';

  return `<span data-type="${tokenType}" data-field="${
    tokenField}" data-label="${tokenLabel}" class="${contentClass}" contenteditable="false" tabindex="-1"></span>`;
} // getFieldTokenHtml()

export function convertFieldTokensToHtml(tokenFields, editTemplateText) {
  if (editTemplateText == null) {
    return '';
  }
  if (Object.keys(tokenFields).length === 0) {
    return editTemplateText;
  }
  const data = Object.fromEntries(
    Object.values(tokenFields).map((field) => [
      field.token,
      getFieldTokenHtml(field.token, field.label, field.tokenType),
    ]),
  );
  return replaceTags(editTemplateText, data);
} // convertFieldTokensToHtml()

export function convertHtmlToFieldTokens(tokenFields, editTemplateText) {
  const data = Object.fromEntries(
    Object.values(tokenFields).map((field) => [
      field.token,
      `{{${field.token}}}`,
    ]),
  );
  return replaceHtml(editTemplateText, data);
} // convertHtmlToFieldTokens()

/**
 * Revert baked dynamic-field images back to their editor placeholder spans.
 *
 * The "render fields" action bakes resolved field values into the content. Image
 * fields resolve to <img> tags carrying a data-field attribute (added by the
 * assessment adapter's renderWithImages). We intentionally do NOT bake images -
 * their URLs point at live assessment storage and aren't durable - so we turn those
 * tagged <img> tags back into dataSection placeholder spans, keeping the field
 * dynamic. User-uploaded images (which have no data-field) are left untouched.
 *
 * @param {String} html Rendered HTML content
 * @param {Array|Object} tokenFields Field definitions (must include image fields)
 * @returns {String} HTML with dynamic-field images reverted to placeholder spans
 */
export function revertRenderedFieldImagesToTokens(html, tokenFields) {
  if (!html) {
    return html;
  }
  return Object.values(tokenFields)
    .filter((field) => field.tokenType === 'dataSection')
    .reduce((acc, field) => acc.replace(
      // Escape the token: it's dot-notation (e.g. assessmentType_x.dataField_y), so an
      // unescaped '.' would be a regex wildcard and could match the wrong <img>.
      new RegExp(`<img [^>]*data-field="${escapeRegExp(field.token)}"[^>]*>`, 'g'),
      // Function replacer so any '$' in the token/label isn't treated as a replacement pattern.
      () => getFieldTokenHtml(field.token, field.label, field.tokenType),
    ), html);
} // revertRenderedFieldImagesToTokens()
